import type { components, operations } from "./schema";
import { apiObservation, configureTelemetry } from "../telemetry";
export type AccessRole = components["schemas"]["AccessRole"];
export type AccessCatalogue = components["schemas"]["AccessCatalogue"];
export type PersonPolicy = components["schemas"]["PersonPolicy"];
export type PersonExceptions = components["schemas"]["PersonExceptions"];
export type RoleEdit = components["schemas"]["RoleEdit"];
export type ExceptionEdit = components["schemas"]["ExceptionEdit"];
export type Evidence = components["schemas"]["Evidence"];
export type ChainFacts = components["schemas"]["ChainFacts"];
export type RunEntry = components["schemas"]["RunEntry"];
export type RunPage = components["schemas"]["RunPage"];
export type Lease = components["schemas"]["Lease"];
export type Viewer = components["schemas"]["Viewer"];
export type PortalRole = components["schemas"]["PortalRole"];
export type PurgeVerdict = components["schemas"]["PurgeVerdict"];
export type Review = components["schemas"]["Review"];
export type RunQuery = NonNullable<operations["runs"]["parameters"]["query"]>;

export type RunDetail = components["schemas"]["RunDetail"];
export type Person = components["schemas"]["Person"];
export type PeoplePage = components["schemas"]["PeoplePage"];
export type OrganizationEdit = components["schemas"]["OrganizationEdit"];
export type OrganizationTeam = components["schemas"]["OrganizationTeam"];
export type TeamEdit = components["schemas"]["TeamEdit"];
export type AccountReviewEdit = components["schemas"]["AccountReviewEdit"];
export type AccountOwnership = components["schemas"]["AccountOwnership"];
export type PersonDetail = components["schemas"]["PersonDetail"];
export type DiscoveryPage = components["schemas"]["DiscoveryPage"];
export type DiscoveredProvider = components["schemas"]["DiscoveredProvider"];
export type DiscoveredAccount = components["schemas"]["DiscoveredAccount"];
export type ScanDetail = components["schemas"]["ScanDetail"];
export type OpenTask = components["schemas"]["OpenTask"];
export type TaskPage = components["schemas"]["TaskPage"];
export type TaskDismissalEdit = components["schemas"]["TaskDismissalEdit"];
export type TaskReference = components["schemas"]["TaskReference"];

export type TaskQuery = NonNullable<operations["tasks"]["parameters"]["query"]>;

/**
 * What HR files, generated from the document like every other payload. A
 * request is a DOCUMENT, never an execution: filing one changes nothing in the
 * directory, and the portal holds no permission that could. An operator reads
 * it, previews the plan, and runs it with the privileged identity that lives
 * outside this application.
 */
export type LifecycleKind = components["schemas"]["LifecycleKind"];
export type RequestState = components["schemas"]["RequestState"];
export type RequestDraft = components["schemas"]["RequestDraft"];
export type LifecycleRequest = components["schemas"]["LifecycleRequest"];
export type RequestPage = components["schemas"]["RequestPage"];
export type RequestPreview = components["schemas"]["RequestPreview"];
export type RequestDetail = components["schemas"]["RequestDetail"];
export type LicenseAvailability = components["schemas"]["LicenseAvailability"];

/** Published descriptions and saved desired policies; execution validates targets. */
export type RoleCatalogue = components["schemas"]["RoleCatalogue"];
export type RoleContents = components["schemas"]["RoleContents"];
export type RoleGrant = components["schemas"]["RoleGrant"];

/**
 * 422 means the stored bytes failed verification here, and 404 that no run with
 * that identifier is in the indexed window. Neither is a transport failure, and
 * the distinction is what the run views render, so the status travels with the
 * message.
 */
export class PortalError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "PortalError";
  }
}

/**
 * A feature the deployment has not turned on, rather than a request that went
 * wrong. Requests and task confirmations both write, and a portal configured
 * without a writable store answers 501 exactly as the directory does.
 */
export function unconfigured(error: Error | null): boolean {
  return error instanceof PortalError && (error.status === 501 || error.status === 404);
}

export type AccessToken = () => Promise<string>;

export interface Portal {
  teams(signal?: AbortSignal): Promise<PeoplePage>;
  saveTeam(edit: TeamEdit, signal?: AbortSignal): Promise<PeoplePage>;
  reviewAccount(edit: AccountReviewEdit, signal?: AbortSignal): Promise<DiscoveryPage>;
  saveOrganization(edit: OrganizationEdit, signal?: AbortSignal): Promise<PeoplePage>;
  discovery(signal?: AbortSignal): Promise<DiscoveryPage>;
  startScan(idempotencyKey: string, signal?: AbortSignal): Promise<ScanDetail>;
  scan(id: string, signal?: AbortSignal): Promise<ScanDetail>;
  accessRoles(signal?: AbortSignal): Promise<AccessCatalogue>;
  saveRole(edit: RoleEdit, signal?: AbortSignal): Promise<AccessCatalogue>;
  personPolicy(upn: string, signal?: AbortSignal): Promise<PersonPolicy>;
  savePersonPolicy(upn: string, edit: ExceptionEdit, signal?: AbortSignal): Promise<PersonPolicy>;
  runs(query: RunQuery, signal?: AbortSignal): Promise<RunPage>;
  run(runId: string, signal?: AbortSignal): Promise<RunDetail>;
  lease(signal?: AbortSignal): Promise<Lease>;
  me(signal?: AbortSignal): Promise<Viewer>;
  /** 501 means this portal has no directory configured, not that nobody exists. */
  people(signal?: AbortSignal): Promise<PeoplePage>;
  person(upn: string, signal?: AbortSignal): Promise<PersonDetail>;
  /** What is outstanding, already folded out of evidence by the server. */
  tasks(query: TaskQuery, signal?: AbortSignal): Promise<TaskPage>;
  updateTaskDismissals(edit: TaskDismissalEdit, query: TaskQuery, signal?: AbortSignal): Promise<TaskPage>;
  /** 501 means requests are not enabled here, not that none have been filed. */
  requests(signal?: AbortSignal, cursor?: string): Promise<RequestPage>;
  requestSchedule(signal?: AbortSignal): Promise<string>;
  request(id: string, signal?: AbortSignal): Promise<RequestDetail>;
  deleteRequest(id: string, signal?: AbortSignal): Promise<LifecycleRequest>;
  licenseAvailability(id: string, signal?: AbortSignal): Promise<LicenseAvailability>;
  /** The jobs a request may name, as the newest passing validation found them. */
  roles(signal?: AbortSignal): Promise<RoleCatalogue>;
  fileRequest(draft: RequestDraft, idempotencyKey: string, signal?: AbortSignal): Promise<LifecycleRequest>;
  reviseRequest(id: string, draft: RequestDraft, idempotencyKey: string, signal?: AbortSignal): Promise<LifecycleRequest>;
  /**
   * Releases a filed request for real execution. Until somebody does, every
   * pass previews it however due it is. Whoever may file may release, their
   * own included: the two are separate acts, not separate people.
   */
  approveRequest(requestId: string, previewRunId: string, signal?: AbortSignal): Promise<void>;
  /**
   * Confirms a manual task the signed-in person did off-system. The server
   * records it as an attestation bound to the subject and the action, and
   * refuses anyone outside the permitted-attestor list; this only names which
   * step was done.
   */
  confirmTask(upn: string, fingerprint: string, signal?: AbortSignal): Promise<void>;
}

function search(query: RunQuery | TaskQuery): string {
  const parameters = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") {
      parameters.set(name, String(value));
    }
  }
  const encoded = parameters.toString();
  return encoded === "" ? "" : `?${encoded}`;
}

export function portal(token: AccessToken, base = ""): Portal {
  async function call<T>(
    route: string,
    init: { method?: string; body?: unknown; signal?: AbortSignal; idempotencyKey?: string } = {},
  ): Promise<T> {
    const accessToken = await token();
    configureTelemetry(accessToken, base);
    const observation = apiObservation(route, init.method ?? "GET");
    const headers: Record<string, string> = {
      ...observation.headers,
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    };
    if (init.body !== undefined) headers["Content-Type"] = "application/json";
    if (init.idempotencyKey !== undefined) headers["Idempotency-Key"] = init.idempotencyKey;
    let response: Response;
    try {
      response = await fetch(`${base}${route}`, {
        method: init.method ?? "GET",
        headers,
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
        // Evidence is immutable, but the index behind it is not, and a stale
        // cached page would misreport a run as still unpublished.
        cache: "no-store",
        signal: init.signal ?? null,
      });
    } catch {
      observation.finish(0, init.signal?.aborted);
      throw new PortalError(0, "the portal is unreachable");
    }
    const body: unknown = await response.json().catch(() => null);
    observation.finish(response.status);
    if (!response.ok) {
      const error =
        body !== null && typeof body === "object" && "error" in body
          ? String((body as { error: unknown }).error)
          : response.statusText;
      throw new PortalError(response.status, error);
    }
    return body as T;
  }

  return {
    teams: signal => call<PeoplePage>("/api/teams", { signal }),
    saveTeam: (edit, signal) => call<PeoplePage>("/api/teams", { method: "PUT", body: edit, signal }),
    reviewAccount: (edit, signal) => call<DiscoveryPage>("/api/discovery/reviews", { method: "PUT", body: edit, signal }),
    saveOrganization: (edit, signal) => call<PeoplePage>("/api/organization", { method: "PUT", body: edit, signal }),
    discovery: signal => call<DiscoveryPage>("/api/discovery", { signal }),
    startScan: (idempotencyKey, signal) => call<ScanDetail>("/api/discovery/scans", { method: "POST", idempotencyKey, signal }),
    scan: (id, signal) => call<ScanDetail>(`/api/discovery/scans/${encodeURIComponent(id)}`, { signal }),
    accessRoles: (signal) => call<AccessCatalogue>("/api/access/roles", { signal }),
    saveRole: (edit, signal) => call<AccessCatalogue>("/api/access/roles", { method: "PUT", body: edit, signal }),
    personPolicy: (upn, signal) => call<PersonPolicy>(`/api/people/${encodeURIComponent(upn)}/access-policy`, { signal }),
    savePersonPolicy: (upn, edit, signal) => call<PersonPolicy>(`/api/people/${encodeURIComponent(upn)}/access-policy`, { method: "PUT", body: edit, signal }),
    runs: (query, signal) => call<RunPage>(`/api/runs${search(query)}`, { signal }),
    run: (runId, signal) =>
      call<RunDetail>(`/api/runs/${encodeURIComponent(runId)}`, { signal }),
    lease: (signal) => call<Lease>("/api/lease", { signal }),
    me: (signal) => call<Viewer>("/api/me", { signal }),
    people: (signal) => call<PeoplePage>("/api/people", { signal }),
    person: (upn, signal) =>
      call<PersonDetail>(`/api/people/${encodeURIComponent(upn)}`, { signal }),
    tasks: (query, signal) => call<TaskPage>(`/api/tasks${search(query)}`, { signal }),
    updateTaskDismissals: (edit, query, signal) => call<TaskPage>(`/api/tasks/dismissals${search(query)}`, { method: "PUT", body: edit, signal }),
    requests: (signal, cursor) => call<RequestPage>(`/api/requests${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, { signal }),
    requestSchedule: (signal) => call<string>("/api/requests/schedule", { signal }),
    request: (id, signal) => call<RequestDetail>(`/api/requests/${encodeURIComponent(id)}`, { signal }),
    deleteRequest: (id, signal) => call<LifecycleRequest>(`/api/requests/${encodeURIComponent(id)}`, { method: "DELETE", signal }),
    licenseAvailability: (id, signal) => call<LicenseAvailability>(`/api/requests/${encodeURIComponent(id)}/license-availability`, { signal }),
    roles: (signal) => call<RoleCatalogue>("/api/roles", { signal }),
    fileRequest: (draft, idempotencyKey, signal) =>
      call<LifecycleRequest>("/api/requests", { method: "POST", body: draft, idempotencyKey, signal }),
    reviseRequest: (id, draft, idempotencyKey, signal) =>
      call<LifecycleRequest>(`/api/requests/${encodeURIComponent(id)}/revisions`, { method: "POST", body: draft, idempotencyKey, signal }),
    approveRequest: (requestId, previewRunId, signal) =>
      call<void>(`/api/requests/${encodeURIComponent(requestId)}/approval`, {
        method: "POST",
        body: { preview_run_id: previewRunId },
        signal,
      }),
    confirmTask: (upn, fingerprint, signal) =>
      call<void>(`/api/people/${encodeURIComponent(upn)}/tasks`, {
        method: "POST",
        body: { action_fingerprint: fingerprint },
        signal,
      }),
  };
}

const RANK: PortalRole[] = ["HR", "Auditor", "Operator", "Admin"];

/** Mirrors `Viewer::authorize`: the server decides, this only hides dead ends. */
export function holds(viewer: Viewer | null, least: PortalRole): boolean {
  const required = RANK.indexOf(least);
  return (viewer?.roles ?? []).some((role) => RANK.indexOf(role) >= required);
}

/** Write capability is independent of the read hierarchy. */
export function canManageRequests(viewer: Viewer | null): boolean {
  return (viewer?.roles ?? []).some((role) => role === "HR" || role === "Operator" || role === "Admin");
}

export function canDeleteRequest(viewer: Viewer | null, request: LifecycleRequest): boolean {
  return !request.deletion && request.state !== "withdrawn" && canManageRequests(viewer) && (request.kind !== "adopt" || holds(viewer, "Operator"));
}

/** Preserve a stale editor rather than overwriting a concurrent policy edit. */
export function accessEditError(cause: unknown): Error {
  if (cause instanceof PortalError && cause.status === 409) {
    return new Error("Access policy changed while you were editing. Cancel and reopen the editor to review the latest version, then save again.");
  }
  return cause instanceof Error ? cause : new Error("Could not save the access policy.");
}
